import { Box, Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import { useCallback, useEffect, useState } from "react";
import SyncIcon from '@mui/icons-material/Sync';
import { Localized, useLocalization } from "@fluent/react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { getWithAuth } from "../../../http";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { DateMenuFilter } from "../../../app/DateMenuFilter";
import { Company, loadCompanies } from "../../company/companiesSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { selectSettings } from "../../settings/settingsSlice";
import { enUS, plPL } from "@mui/x-data-grid/locales";
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar";
import { DotColorChip } from "../../../app/components/DotColorChip";
import { CancelButton } from "../../../app/components/CancelButton";
import { ErrorContainer } from "../../../app/ErrorContainer";

export interface Mail {
    id: number
    mailBody: string,
    mailTo: string,
    mailType: string,
    mailSubject: string
    sendBy: string,
    sendDate: Date,
    status: string,
    message: string
}

const MailLogStatus = ({ status }: { status: string | undefined }) => {
    const normalizedStatus = status?.toLowerCase();
    const isSent = normalizedStatus === 'sent';
    const isError = normalizedStatus === 'error';
    const isNotSent = normalizedStatus === 'notsent'; 

    return (
        <DotColorChip
            l10nId={isSent ? "mail-status-sent" : isError ? "mail-status-error" : isNotSent ? "mail-status-not-sent" : ''}
            label={isSent ? "Wysłano" : isError ? "Błąd" : isNotSent ? "Niewysłano" : ""}
            color={isSent ? "success" : isError ? "error" : isNotSent ? "warning" : "default"}
        />
    );
};

export const MailLogs = () => {
    const { locale } = useAppSelector(selectSettings);
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()

    const [rows, setRows] = useState<Mail[]>([]);
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);
    const [currentRow, setCurrentRow] = useState<Mail | null>(null);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [mailType, setMailType] = useState('all');
    const [mailTypes, setMailTypes] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [showHtml, setShowHtml] = useState(false);
    const [company, setCompany] = useState('all');
    const [isError, setIsError] = useState<boolean>(false);
    const [error, setError] = useState<any>(null);

    const currentDate = new Date();
    var dateFromFilter = new Date();
    dateFromFilter.setDate(currentDate.getDate() - 30);
    const [dateFrom, setDateFrom] = useState(dateFromFilter);
    const [dateTo, setDateTo] = useState(new Date());

    useEffect(() => {
        dispatch(getWithAuth({
            url: `api/sys/mail-logs/types`
        })).then(result => {
            if (getWithAuth.fulfilled.match(result)) {
                const { payload } = result
                setMailTypes(payload as string[])
            }
        })
        dispatch(loadCompanies())
            .then(unwrapResult)
            .then(setCompanies)
            .catch((error) => console.error(error))
    }, [dispatch])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const handleSubmit = (date) => {
        setDateFrom(date.dateFrom);
        setDateTo(date.dateTo);
    }

    const handleMailTypeChange = (event) => {
        setMailType(event.target.value);
    };

    const handleCompanyChange = (event) => {
        setCompany(event.target.value);
    };

    const handleRefreshClick = async () => {
        setIsLoading(true);
        setIsError(false);
        setError(null);
        try {
            const result = await dispatch(getWithAuth({
                url: `api/sys/mail-logs?${company !== "all" ? 'companyId=' + company + '&' : ''}mailType=${mailType}&dateFrom=${dateFrom.toISOString()}&dateTo=${dateTo.toISOString()}`
            }))
            if (getWithAuth.fulfilled.match(result)) {
                const { payload } = result
                setRows(payload as Mail[])
            } else {
                setIsError(true);
                setError(result.payload);
            }
        } catch(err) {
            setIsError(true);
            setError(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleShowDetails = useCallback((row: Mail) => {
        setCurrentRow(row)
        setShowDetailsDialog(true)
    }, [setShowDetailsDialog])

    const handleCancelPurposeClick = async () => {
        setCurrentRow(null)
        setShowDetailsDialog(false)
        setShowHtml(false)
    }

    const columns: GridColDef[] =
        [
            {
                field: 'sendDate',
                headerName: l10n.getString("mail-date", null, "Data"),
                flex: 1,
                renderCell: (params) => {
                    return params ? format(new Date(params.value), 'dd/MM/yyyy HH:mm:ss') : null;  // formatujemy do wyglądu daty i czasu
                }
            },
            {
                field: 'mailType',
                headerName: l10n.getString("mail-type", null, "Typ"),
                flex: 1
            },
            {
                field: 'mailTypeTranslation',
                headerName: l10n.getString("mail-template-name", null, "_Nazwa"),
                flex: 1,
                valueGetter: (_, row) => {
                    return l10n.getString(`mail-type-${row.mailType}`, null, `_${row.mailType}`)
                }
            },
            {
                field: 'mailTo',
                headerName: l10n.getString("mail-recipient", null, "Adresat"),
                flex: 1
            },
            {
                field: 'status',
                headerName: l10n.getString("mail-status", null, "Status"),
                width: 200,
                renderCell: (params) => {
                    const status = params.value as string;
                    
                    return MailLogStatus({ status })
                }
            },
            {
                field: 'actions', type: 'actions', width: 200,
                renderCell: (params) => {
                    return (
                        <ButtonGroup>
                            <Button color="secondary" variant="outlined" onClick={() => handleShowDetails(params.row)} >
                                <Localized id="details">
                                    <span>Szczegóły</span>
                                </Localized>
                            </Button>
                        </ButtonGroup>
                    )
                }
            }
        ];

    if(isLoading) {
        return <LoadingContainer/>
    }

    if(isError) {
        return <ErrorContainer error={error}/>
    }

    return (
        <>
            <div>
                <Stack direction="row" spacing={2} sx={{ marginBottom: 2 }}>
                    <FormControl>
                        <InputLabel id="select-company-label"><Localized id="company">Firma</Localized></InputLabel>
                        <Select
                            value={company}
                            onChange={handleCompanyChange}
                            labelId="select-company-label"
                            label={<Localized id="company">Firma</Localized>}
                            sx={{ width: '200px' }}
                        >
                            <MenuItem value={'all'}>
                                <Localized id="all">All</Localized>
                            </MenuItem>
                            {companies.map((option) =>
                                <MenuItem value={option.id}>{option.name}</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <FormControl>
                        <InputLabel id="select-mail-type-label"><Localized id="mail-type">Typ</Localized></InputLabel>
                        <Select
                            value={mailType}
                            onChange={handleMailTypeChange}
                            labelId="select-mail-type-label"
                            label={<Localized id="mail-type">Typ</Localized>}
                            sx={{ width: '200px' }}
                        >
                            <MenuItem value={'all'}>
                                <Localized id="all">All</Localized>
                            </MenuItem>
                            {mailTypes.map((option) =>
                                <MenuItem value={option}>{option}</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <DateMenuFilter onSubmit={handleSubmit} />
                    <IconButton
                        onClick={handleRefreshClick}>
                        <SyncIcon />
                    </IconButton>
                </Stack>
            </div >
            <div style={{ flexGrow: 1 }}>
                <DataGrid style={{ minHeight: 100, height: 'calc(100vh - 280px)' }} localeText={getCustomLocaleText()} initialState={{ sorting: { sortModel: [{ field: 'sendDate', sort: 'desc' }] } }} rows={rows} columns={columns} slots={{ toolbar: QuickSearchToolbar }} />
            </div>
            <Dialog open={showDetailsDialog} fullWidth maxWidth="lg">
                <DialogTitle>
                    <Localized id="mail-details">Szczegóły maila</Localized>
                </DialogTitle>
                <DialogContent>
                    <Stack direction="column" spacing={2} padding={2}>
                        <TextField
                            fullWidth
                            disabled
                            value={currentRow?.mailSubject}
                            label={<Localized id="mail-subject">Temat</Localized>}
                        />

                        <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2">
                                    <Localized id="mail-content">Treść</Localized>
                                </Typography>
                                <ToggleButtonGroup
                                    size="small"
                                    exclusive
                                    value={showHtml ? "html" : "preview"}
                                    onChange={(_, value) => value && setShowHtml(value === "html")}
                                    aria-label="mail content view"
                                >
                                    <ToggleButton value="preview">
                                        <Localized id="mail-preview">Podgląd</Localized>
                                    </ToggleButton>
                                    <ToggleButton value="html">HTML</ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                            <Box sx={{
                                p: 2,
                                minHeight: 120,
                                maxHeight: 360,
                                overflow: 'auto',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                backgroundColor: showHtml ? 'grey.50' : '#f9f9f9'
                            }}>
                                {showHtml ? (
                                    <Typography component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                        {currentRow?.mailBody ?? ''}
                                    </Typography>
                                ) : (
                                    <div dangerouslySetInnerHTML={{ __html: currentRow?.mailBody ?? '' }} />
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ pt: 1 }}>
                            <Typography variant="h6" sx={{ mb: 1.5 }}>
                                <Localized id="mail-sending-details">Szczegóły wysyłki</Localized>
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField
                                    fullWidth
                                    disabled
                                    value={currentRow?.mailTo ?? ''}
                                    label={<Localized id="mail-recipient">Adresat</Localized>}
                                />
                                <TextField
                                    fullWidth
                                    disabled
                                    value={currentRow?.sendDate ? format(new Date(currentRow.sendDate), 'dd/MM/yyyy HH:mm:ss') : ''}
                                    label={<Localized id="mail-date">Data wysyłki</Localized>}
                                />
                                <Box sx={{ minWidth: 150, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        <Localized id="mail-status">Status</Localized>
                                    </Typography>
                                    <MailLogStatus status={currentRow?.status} />
                                </Box>
                            </Stack>
                            <TextField
                                fullWidth
                                disabled
                                multiline
                                minRows={2}
                                sx={{ mt: 2 }}
                                value={currentRow?.message ?? ''}
                                label={<Localized id="mail-additional-information">Dodatkowe informacje</Localized>}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>

                    </Stack>
                </DialogContent>
                <DialogActions>
                    <CancelButton loading={isLoading} onClick={handleCancelPurposeClick}/>
                </DialogActions>
            </Dialog>
        </>
    )
}
